import { useState, useEffect } from "react";
import { CircularProgress } from "@material-ui/core";
import CustomInput from "./CustomInput";
import { formatAlphanumericInput } from "../utils/Utils";
import { validateCoupon } from "../crud/auth.crud";

/**
 * Componente de input e validação de cupom de desconto.
 *
 * Props:
 *  - appliedCoupon: objeto do cupom aplicado (controlado pelo pai)
 *  - onCouponChange(coupon): callback chamado quando o cupom é aplicado ou removido (null = removido)
 *  - disabled: bool
 *  - isMobileScreen: bool
 *  - activeCoupon: (opcional) cupom ativo no plano atual - exibe banner informativo
 *  - activeCouponLabel: (opcional) texto extra no banner (ex: "válido por X meses")
 *  - initialCode: (opcional) código pré-preenchido; se não houver appliedCoupon, dispara validação automática
 *
 * Nota: para resetar o componente (ex: ao trocar de plano), use a prop `key` no pai.
 */
const CouponInput = ({ appliedCoupon, onCouponChange, disabled, isMobileScreen, activeCoupon, activeCouponLabel, initialCode }) => {
    const [couponCode, setCouponCode] = useState(appliedCoupon?.cpd_code ?? initialCode ?? "");
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponError, setCouponError] = useState("");

    useEffect(() => {
        if (initialCode && !appliedCoupon) {
            applyCoupon(initialCode);
        }
    }, []);

    const applyCoupon = (code) => {
        const trimmed = (code ?? couponCode).trim();
        if (!trimmed) return;

        setCouponError("");
        setCouponLoading(true);

        validateCoupon(trimmed)
            .then((res) => {
                onCouponChange?.(res.data);
                setCouponError("");
            })
            .catch((err) => {
                onCouponChange?.(null);
                setCouponError(err?.response?.data?.errors?.[0] ?? "Cupom inválido ou expirado.");
            })
            .finally(() => setCouponLoading(false));
    };

    const showActiveBanner = activeCoupon && appliedCoupon?.cpd_code === activeCoupon.cpd_code;
    const showSuccess = appliedCoupon && !couponError && (!activeCoupon || appliedCoupon.cpd_code !== activeCoupon.cpd_code);

    return (
        <div className="d-flex flex-column gap-8">
            <span className="tt-16 medium color-dark-6">Cupom de desconto</span>

            {showActiveBanner && (
                <div className="d-flex align-items-center gap-8 pil-12" style={{ backgroundColor: "#0D1F13", border: "1px solid #166534", borderRadius: 8, paddingBlock: 10 }}>
                    <span className="tt-14" style={{ color: "#13C296" }}>
                        Cupom <b>{activeCoupon.cpd_name}</b> ativo no plano atual
                        {activeCouponLabel ? ` · ${activeCouponLabel}` : ""}
                    </span>
                </div>
            )}

            <div className={`d-flex gap-12 ${isMobileScreen ? "flex-column" : ""}`}>
                <div style={{ flex: 1 }}>
                    <CustomInput
                        placeholder="Insira o código do cupom"
                        name="coupon_code"
                        value={couponCode}
                        handleChange={(e) => {
                            setCouponCode(formatAlphanumericInput(e).target.value.toUpperCase());
                            if (appliedCoupon?.cpd_code !== e.target.value.toUpperCase()) {
                                onCouponChange?.(null);
                                setCouponError("");
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                applyCoupon();
                            }
                        }}
                        disabled={disabled || couponLoading}
                    />
                </div>
                <button
                    type="button"
                    className="btn btn-border tt-16 medium"
                    style={{ whiteSpace: "nowrap" }}
                    onClick={() => applyCoupon()}
                    disabled={disabled || couponLoading || !couponCode.trim() || !!appliedCoupon}
                >
                    Aplicar cupom {couponLoading ? <CircularProgress size={14} className="margin-icon-reverse" style={{ color: "#fff" }} /> : ""}
                </button>
            </div>

            {couponError && (
                <span className="tt-14" style={{ color: "#f23030" }}>
                    {couponError}
                </span>
            )}
            {showSuccess && (
                <span className="tt-14" style={{ color: "#13C296" }}>
                    Cupom <b>{appliedCoupon.cpd_name}</b> aplicado com sucesso!
                </span>
            )}
        </div>
    );
};

export default CouponInput;
